/**
 * Strategy Builder API — CRUD for composable strategy definitions.
 */

import { getAccessToken, useAuthStore } from '../stores/authStore';

const BASE = '/api';

/**
 * fetch() that attaches the user's JWT and transparently refreshes it on a
 * 401, mirroring portfoliosApi's authFetch. The gateway's mutating strategy
 * routes (notably DELETE) are auth-gated and forward `x-user-id` to the
 * engine, so unauthenticated calls 401 ("Failed to delete strategy: 401").
 * Read-only GETs don't strictly need it, but sending the token is harmless.
 */
async function authFetch(
  path: string,
  opts: { method?: string; body?: unknown } = {},
): Promise<Response> {
  const token = getAccessToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const init: RequestInit = {
    method: opts.method ?? 'GET',
    headers,
    credentials: 'include',
  };
  if (opts.body != null) init.body = JSON.stringify(opts.body);

  const res = await fetch(path, init);
  if (res.status === 401 && token) {
    const refreshRes = await fetch('/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });
    if (refreshRes.ok) {
      const { accessToken } = (await refreshRes.json()) as { accessToken: string };
      useAuthStore.getState().setAccessToken(accessToken);
      const retryHeaders = { ...headers, Authorization: `Bearer ${accessToken}` };
      return fetch(path, { ...init, headers: retryHeaders });
    }
  }
  return res;
}

// ── Types ──────────────────────────────────────────────────────

export type StrategyComponent = {
  id: string;
  weight: number;
  params: Record<string, number>;
};

/** The kind of engine that runs this strategy. `composable` = per-symbol
 *  indicator scoring; the others are self-contained engines with their own
 *  config block below. */
export type StrategyType =
  | 'composable'
  | 'cross_sectional'
  | 'dca'
  | 'grid'
  | 'ml';

/** Regime filter that can switch a basket flat. `vol_calm` = only trade when
 *  the gate symbol is calm; `btc_trend` = only trade when it's trending up. */
export type RegimeGate = {
  mode: 'vol_calm' | 'btc_trend';
  symbol: string;
  lookbackBars: number;
};

/** Cross-sectional (momentum-basket) config: rank a universe, hold the top-K,
 *  rebalance on a schedule, optionally gated by a market regime. */
export type CrossSectionalConfig = {
  rankSignal: 'momentum' | 'mean_reversion';
  rankLookbackBars: number;
  rankTimeframe: number; // minutes
  topK: number;
  rebalanceEveryBars: number;
  universe: string[];
  regimeGate?: RegimeGate;
  basketStopLossPct?: number;
  basketTakeProfitPct?: number;
  basketTrailing?: boolean;
};

export type DcaConfig = {
  amount: string;
  frequency: string;
  totalBudget: string;
  buyDipBoost: boolean;
  dipMultiplier: number;
  takeProfitPct: number;
  stopLossPct: number;
  smartTiming: boolean;
};

export type GridConfig = {
  gridType: 'arithmetic' | 'geometric';
  upperBound: string;
  lowerBound: string;
  gridLines: number;
  investmentPerGrid: string;
  totalInvestment: string;
  trailing: boolean;
  rebalanceThreshold: number;
};

export type StrategyDefinition = {
  _id: string;
  name: string;
  /** User-chosen display name. When set, UIs show this instead of `name`. */
  customName?: string;
  description: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  goal: 'conservative' | 'balanced' | 'aggressive' | 'scalp' | 'custom';
  components: StrategyComponent[];
  tfWeights: Record<string, number>;
  buyThreshold: number;
  sellThreshold: number;
  riskPerTradePct: number;
  slAtrMultiplier: number;
  tpAtrMultiplier: number;
  trailingActivationAtr: number;
  trailingStepAtr: number;
  maxDrawdownPct: number;
  cooldownCandles: number;
  enabled: boolean;
  assignedSymbols: string[];
  // Fields the engine returns that classify and configure non-composable
  // strategies. Optional because older/simpler docs may omit them.
  strategyType?: StrategyType;
  mode?: 'paper' | 'live';
  market?: 'spot' | 'futures';
  allowedSides?: ('long' | 'short')[];
  origin?: string;
  crossSectionalConfig?: CrossSectionalConfig;
  dcaConfig?: DcaConfig;
  gridConfig?: GridConfig;
};

export type ComponentMeta = {
  id: string;
  name: string;
  description: string;
  helpText: string;
  category: 'trend' | 'oscillator' | 'volume' | 'structure' | 'sentiment';
  requiredIndicators: string[];
};

export type GoalPreset = {
  goal: string;
  name: string;
  description: string;
  helpText: string;
  components: StrategyComponent[];
  tfWeights: Record<string, number>;
  buyThreshold: number;
  sellThreshold: number;
  riskPerTradePct: number;
  slAtrMultiplier: number;
  tpAtrMultiplier: number;
  trailingActivationAtr: number;
  trailingStepAtr: number;
  maxDrawdownPct: number;
  cooldownCandles: number;
};

// ── API calls ──────────────────────────────────────────────────

export async function fetchStrategies(): Promise<StrategyDefinition[]> {
  const res = await fetch(`${BASE}/strategies`);
  if (!res.ok) throw new Error(`Failed to fetch strategies: ${res.status}`);
  return res.json();
}

export async function fetchStrategy(id: string): Promise<StrategyDefinition> {
  const res = await fetch(`${BASE}/strategies/${id}`);
  if (!res.ok) throw new Error(`Failed to fetch strategy: ${res.status}`);
  return res.json();
}

export async function fetchComponents(): Promise<ComponentMeta[]> {
  const res = await fetch(`${BASE}/strategies/components`);
  if (!res.ok) throw new Error(`Failed to fetch components: ${res.status}`);
  return res.json();
}

export async function fetchPresets(): Promise<Record<string, GoalPreset>> {
  const res = await fetch(`${BASE}/strategies/presets`);
  if (!res.ok) throw new Error(`Failed to fetch presets: ${res.status}`);
  return res.json();
}

export async function createStrategy(
  body: Partial<StrategyDefinition>,
): Promise<StrategyDefinition> {
  const res = await authFetch(`${BASE}/strategies`, { method: 'POST', body });
  if (!res.ok) throw new Error(`Failed to create strategy: ${res.status}`);
  return res.json();
}

export async function updateStrategy(
  id: string,
  body: Partial<StrategyDefinition>,
): Promise<StrategyDefinition> {
  const res = await authFetch(`${BASE}/strategies/${id}`, { method: 'PUT', body });
  if (!res.ok) throw new Error(`Failed to update strategy: ${res.status}`);
  return res.json();
}

/**
 * Delete a strategy. The gateway route is auth-gated (forwards x-user-id so
 * the engine owner-checks), so this MUST carry the JWT. `portfolioIds` lists
 * the wallets the strategy is active on; the gateway detaches it from each
 * before deleting so a position-bearing wallet unwinds cleanly.
 */
export async function deleteStrategy(
  id: string,
  portfolioIds: string[] = [],
): Promise<void> {
  const res = await authFetch(`${BASE}/strategies/${id}`, {
    method: 'DELETE',
    body: { portfolioIds },
  });
  if (!res.ok) throw new Error(`Failed to delete strategy: ${res.status}`);
}
